import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { ConfirmModal } from '@/components/ui/Modal'
import { Spinner } from '@/components/ui/Spinner'
import { Table } from '@/components/ui/Table'
import { useUrlFilters } from '@/hooks/useUrlFilters'
import { toastService } from '@/lib/toastService'
import { Box, Heading, HStack, IconButton, Stack, Text } from '@chakra-ui/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createColumnHelper } from '@tanstack/react-table'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FiEdit2, FiPlus, FiTrash2 } from 'react-icons/fi'
import { deleteDeveloper, getDevelopers } from '../api/developersApi'
import { DeveloperModal } from '../components/DeveloperModal'

const columnHelper = createColumnHelper()

export function Developers() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingDeveloper, setEditingDeveloper] = useState(null)
  const [deleteConfirm, setDeleteConfirm] = useState(null)

  // Use URL filters hook
  const { filters, updateFilters, handlePaginationChange, handleSortingChange } = useUrlFilters({
    search: '',
    page: 1,
    page_size: 20,
    ordering: 'name',
  })

  // Fetch developers with filters and pagination
  const { data, isLoading } = useQuery({
    queryKey: ['developers', filters.search, filters.page, filters.page_size, filters.ordering],
    queryFn: () => {
      const params = {
        page: filters.page,
        page_size: filters.page_size,
      }
      if (filters.search) params.search = filters.search
      if (filters.ordering) params.ordering = filters.ordering
      return getDevelopers(params)
    },
  })

  const totalCount = data?.meta?.pagination?.total_count || 0
  const pageCount = Math.ceil(totalCount / filters.page_size)

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: deleteDeveloper,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['developers'] })
      toastService.success({
        title: t('common.success'),
        description: t('developers.developerDeleted'),
      })
      setDeleteConfirm(null)
    },
    onError: (error) => {
      toastService.error({
        title: t('common.error'),
        description: error.message || t('developers.developerDeleteError'),
      })
    },
  })

  const handleEdit = (developer) => {
    setEditingDeveloper(developer)
    setIsModalOpen(true)
  }

  const handleCreate = () => {
    setEditingDeveloper(null)
    setIsModalOpen(true)
  }

  const handleDelete = (developer) => {
    setDeleteConfirm(developer)
  }

  const confirmDelete = () => {
    if (deleteConfirm) {
      deleteMutation.mutate(deleteConfirm.id)
    }
  }

  const columns = [
    columnHelper.accessor('name', {
      header: t('developers.name'),
      cell: (info) => (
        <Text fontWeight="600" color="#172B4D">
          {info.getValue()}
        </Text>
      ),
    }),
    columnHelper.display({
      id: 'actions',
      header: t('common.actions'),
      cell: (info) => (
        <HStack spacing={2}>
          <IconButton
            aria-label={t('common.edit')}
            size="sm"
            variant="ghost"
            onClick={() => handleEdit(info.row.original)}
          >
            <FiEdit2 />
          </IconButton>
          <IconButton
            aria-label={t('common.delete')}
            size="sm"
            variant="ghost"
            colorScheme="red"
            onClick={() => handleDelete(info.row.original)}
          >
            <FiTrash2 />
          </IconButton>
        </HStack>
      ),
    }),
  ]

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minH="400px">
        <Spinner size="xl" />
      </Box>
    )
  }

  return (
    <Box p={8}>
      <HStack justify="space-between" mb={6}>
        <Heading size="2xl">{t('developers.title')}</Heading>
        <Button size="md" onClick={handleCreate} leftIcon={<FiPlus />}>
          {t('developers.createDeveloper')}
        </Button>
      </HStack>

      {/* Filters */}
      <Box bg="white" p={4} borderRadius="md" border="1px solid" borderColor="#DFE1E6" mb={4}>
        <Stack direction={{ base: 'column', md: 'row' }} gap={4}>
          <Box flex="1">
            <Input
              placeholder={t('developers.searchPlaceholder')}
              value={filters.search}
              onChange={(e) => updateFilters({ search: e.target.value })}
            />
          </Box>
        </Stack>
      </Box>

      <Box bg="white" borderRadius="md" border="1px solid" borderColor="#DFE1E6">
        <Table
          data={data?.data || []}
          columns={columns}
          enablePagination
          enableSorting
          manualPagination
          manualSorting
          pageCount={pageCount}
          totalCount={totalCount}
          state={{
            pagination: {
              pageIndex: filters.page - 1,
              pageSize: filters.page_size,
            },
            sorting: filters.ordering ? [{
              id: filters.ordering.startsWith('-') ? filters.ordering.slice(1) : filters.ordering,
              desc: filters.ordering.startsWith('-')
            }] : [],
          }}
          onPaginationChange={handlePaginationChange}
          onSortingChange={handleSortingChange}
        />
      </Box>

      {/* Create/Edit Modal */}
      <DeveloperModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false)
          setEditingDeveloper(null)
        }}
        developer={editingDeveloper}
      />

      {/* Delete Confirmation */}
      <ConfirmModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={confirmDelete}
        title={t('developers.deleteConfirmTitle')}
        message={t('developers.deleteConfirmMessage', { name: deleteConfirm?.name })}
        confirmText={t('common.delete')}
        isLoading={deleteMutation.isPending}
      />
    </Box>
  )
}

export default Developers
